/* eslint-disable react/jsx-max-depth */
import { useCallback, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

import { environment } from '../../../../environment';
import { formatDateTime } from '../../../../utils/format-date-time.util';
import type { PrinterContextProvider_PdfPrint } from '../../../../contexts/PrinterContext';

import { UserName } from '../../../UserName';
import { Memo } from '../../../Memo';

interface Props {
  readonly onDrawerOpen: (pdfUrl: string) => void;
  readonly row: PrinterContextProvider_PdfPrint;
}

export const TableRow = ({
  onDrawerOpen: handleDrawerOpen,
  row,
}: Readonly<Props>) => {
  const [open, setOpen] = useState(false);

  const openCloseDetails = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);
  const pdfUrl = `${environment.backendUrl}/${row.file.uri}`;
  const viewPdf = useCallback(() => {
    handleDrawerOpen(pdfUrl);
  }, [handleDrawerOpen, pdfUrl]);

  return (
    <>
      <MuiTableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          '&:hover': { background: (theme) => theme.palette.grey[200] },
        }}
      >
        <TableCell>
          <Tooltip
            title={open ? 'Dissimuler les memos' : 'Consulter les memos'}
          >
            <IconButton
              aria-label={open ? 'Dissimuler les memos' : 'Consulter les memos'}
              onClick={openCloseDetails}
              size="small"
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell align="right" component="th" scope="row">
          {row.increment}
        </TableCell>
        <TableCell align="left">{formatDateTime(row.createdAt)}</TableCell>
        <TableCell align="left">
          <UserName id={row.creatorId} />
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={3} sx={{ justifyContent: 'center' }}>
            <Tooltip title="Consulter le PDF">
              <IconButton aria-label="Consulter le PDF" onClick={viewPdf}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Télécharger le PDF">
              <IconButton
                aria-label="Télécharger le PDF"
                href={`${pdfUrl}/download`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <CloudDownloadIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </MuiTableRow>
      <MuiTableRow>
        <TableCell colSpan={6} sx={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Memo
                content={{
                  auto: row.automaticMemo,
                  internal: row.internalMemo,
                  memo: row.memo,
                }}
                display={{ auto: true, internal: true, memo: true }}
              />
            </Box>
          </Collapse>
        </TableCell>
      </MuiTableRow>
    </>
  );
};
